import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useReferenceStore, ReferenceStore } from '../store/useReferenceStore';
import { X, Plus, Search, Save, Eraser } from 'lucide-react';
import toast from 'react-hot-toast';
import { useOverlayClose } from './useOverlayClose';

/**
 * Right-docked modal version of ReferenceDropdown.
 *
 * Same "add to list" semantics — pick an existing entry or add one that
 * persists — but as a panel, for places where the field is a grid cell far too
 * narrow to host an inline dropdown. Adds go through the store's addOption so
 * they get the ${category}_${slug} key that keeps same-named entries in
 * different lists independent.
 */
interface Props {
  title: string;
  /** Which cell is being edited, e.g. "1-May-26 · Friday". */
  subtitle?: string;
  category: string;
  value: string;
  /** Called with the chosen label, or '' to clear the field. */
  onSelect: (value: string) => void;
  onClose: () => void;
}

const ReferenceListModal: React.FC<Props> = ({
  title, subtitle, category, value, onSelect, onClose,
}) => {
  const options = useReferenceStore((state: ReferenceStore) => state.options);
  const addOption = useReferenceStore((state: ReferenceStore) => state.addOption);
  const deleteOption = useReferenceStore((state: ReferenceStore) => state.deleteOption);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const overlayClose = useOverlayClose(onClose);

  const list = options[category] || [];

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return list;
    return list.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, list]);

  const exactMatchExists = list.some(
    (opt) => opt.label.toLowerCase().trim() === searchTerm.toLowerCase().trim(),
  );

  // Values typed before this list existed aren't on it. Surface the current one
  // at the top so it stays visible and can be promoted to the list in one click.
  const legacyValue =
    value && !list.some((opt) => opt.label.toLowerCase().trim() === value.toLowerCase().trim())
      ? value
      : null;

  const saveToList = async (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setIsSaving(true);
    try {
      await addOption(category, trimmed);
      onSelect(trimmed);
      toast.success('Saved to list');
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (key: string, label: string) => {
    try {
      await deleteOption(category, key);
      toast.success('Removed from list');
      // Cells already carrying this text keep it — only the list entry goes.
      if (value === label) searchRef.current?.focus();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const optionRow = (rowKey: string, label: string, onPick: () => void, right?: React.ReactNode) => {
    const selected = value === label;
    return (
      <div
        key={rowKey}
        style={{
          padding: '10px 12px', fontSize: '0.85rem', cursor: 'pointer',
          borderBottom: '1px solid #f1f5f9',
          backgroundColor: selected ? '#eff6ff' : 'transparent',
          color: selected ? '#1d4ed8' : '#1e293b',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = selected ? '#dbeafe' : '#f8fafc'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selected ? '#eff6ff' : 'transparent'; }}
      >
        <span onClick={onPick} style={{ flex: 1, wordBreak: 'break-word' }}>{label}</span>
        {right}
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(2px)', zIndex: 2500,
        display: 'flex', justifyContent: 'flex-end', alignItems: 'stretch',
      }}
      {...overlayClose}
    >
      <div
        style={{
          width: '420px', maxWidth: '92vw', height: '100%', backgroundColor: 'white',
          borderLeft: '1px solid #e2e8f0', boxShadow: '-20px 0 50px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: '1rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0',
        }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.15rem' }}>{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8',
              padding: '0.15rem', display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#0f172a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={15} style={{ position: 'absolute', left: '10px', color: '#94a3b8' }} />
            <input
              ref={searchRef}
              type="text"
              placeholder={`Search or type a new ${title.toLowerCase()}…`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Enter picks the only match, or adds what was typed.
                if (filteredOptions.length === 1) onSelect(filteredOptions[0].label);
                else if (searchTerm.trim() && !exactMatchExists) saveToList(searchTerm);
              }}
              style={{
                width: '100%', padding: '0.55rem 0.75rem 0.55rem 2rem', borderRadius: '6px',
                border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none',
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Options */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {legacyValue && !searchTerm && (
            <div style={{ borderBottom: '1px solid #e2e8f0' }}>
              <div style={{
                padding: '0.5rem 12px 0.25rem', fontSize: '0.68rem', fontWeight: 700,
                color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                Current value — not on the list
              </div>
              {optionRow(
                '__legacy__',
                legacyValue,
                () => onSelect(legacyValue),
                <button
                  onClick={() => saveToList(legacyValue)}
                  disabled={isSaving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
                    border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb',
                    borderRadius: '6px', padding: '0.2rem 0.45rem', fontSize: '0.72rem',
                    fontWeight: 600, cursor: isSaving ? 'wait' : 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <Plus size={12} /> Add to list
                </button>,
              )}
            </div>
          )}

          {filteredOptions.map((opt) =>
            optionRow(
              opt.key,
              opt.label,
              () => onSelect(opt.label),
              <X
                size={14}
                style={{ color: '#94a3b8', flexShrink: 0, cursor: 'pointer' }}
                onClick={() => handleDelete(opt.key, opt.label)}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
              />,
            ),
          )}

          {filteredOptions.length === 0 && (
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>
              {searchTerm ? 'No match — add it below.' : 'No options yet. Type above to add the first one.'}
            </div>
          )}
        </div>

        {/* Footer — pinned so it stays reachable however long the list gets */}
        <div style={{ borderTop: '1px solid #e2e8f0' }}>
          {searchTerm.trim() && !exactMatchExists && (
            <div
              onClick={() => saveToList(searchTerm)}
              style={{
                padding: '12px 1.25rem', backgroundColor: '#eff6ff', color: '#2563eb',
                fontWeight: 600, fontSize: '0.82rem', cursor: isSaving ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus size={16} />
                Add "{searchTerm.trim()}" permanently
              </span>
              <Save size={14} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.85rem 1.25rem' }}>
            <button
              onClick={() => onSelect('')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569',
                borderRadius: '6px', padding: '0.45rem 0.8rem', fontSize: '0.82rem',
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <Eraser size={14} /> Clear
            </button>
            <button
              onClick={onClose}
              style={{
                marginLeft: 'auto', border: 'none', background: '#1e3a8a', color: 'white',
                borderRadius: '6px', padding: '0.45rem 1rem', fontSize: '0.82rem',
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceListModal;
